import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Lucide from "lucide-react";
import { ArrowRight, Phone, Star, CheckCircle2, MessageCircle } from "lucide-react";
import { CLINIC, SERVICE_KEYS, REASON_KEYS } from "../lib/clinic";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { getUtms } from "../lib/utms";
import { SERVICE_LANDING } from "../lib/serviceLanding";import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`transition-all duration-700 ease-out ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}>
      {children}
    </div>
  );
}

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  useSEO({
    title: t("seo.title"),
    description: t("seo.description"),
  });

  // Scroll to hash section when arriving from a landing page (e.g. /#marcacao)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return true; }
      return false;
    };
    if (!tryScroll()) setTimeout(tryScroll, 250);
  }, [location.hash]);

  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("form.consentRequired"));
      trackEvent("form_error", { source: "form_marcacao", error_type: "validation_consent" });
      return;
    }
    if (!form.phone || form.phone.trim().length < 6) {
      toast.error(t("form.toastErr"));
      trackEvent("form_error", { source: "form_marcacao", error_type: "validation_missing_phone" });
      return;
    }
    // Dedupe: prevent double-submit within 30s window
    try {
      const last = parseInt(sessionStorage.getItem("lead_submitted_at") || "0", 10);
      if (last && Date.now() - last < 30000) {
        setDone(true);
        return;
      }
    } catch { /* sessionStorage unavailable — proceed */ }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        service: form.service,
        ...(form.email ? { email: form.email } : {}),
        ...(form.message ? { message: form.message } : {}),
      };
      await api.post("/appointments", payload);
      try { sessionStorage.setItem("lead_submitted_at", String(Date.now())); } catch { /* noop */ }
      setDone(true);
      trackEvent("generate_lead", {
        source: "form_marcacao",
        service: form.service,
        contact_method: "form",
        lead_type: "appointment_request",
        has_email: !!form.email,
        page_title: document.title,
        ...getUtms(),
      });
      toast.success(t("form.toastOk"));
      setForm({ name: "", phone: "", service: "", message: "", email: "" });
      setConsent(false);
    } catch (err) {
      toast.error(t("form.toastErr"));
      const errType = err && err.message && err.message.toLowerCase().includes("network") ? "network_error" : "api_error";
      trackEvent("form_error", { source: "form_marcacao", error_type: errType });
    }
    finally { setLoading(false); }
  };

  const appointmentOptions = t("appointmentOptions", { returnObjects: true });

  const heroBadges = [
    { icon: <Lucide.Brain size={15} strokeWidth={2} className="text-gold-500 shrink-0" />, t: t("hero.b1") },
    { icon: <Lucide.CheckCircle2 size={15} strokeWidth={2} className="text-emerald-500 shrink-0" />, t: t("hero.b2") },
    { icon: <span className="shrink-0 w-[15px] h-[15px] rounded-[3px] bg-[#0066CC] text-white inline-flex items-center justify-center text-[10px] font-bold leading-none">P</span>, t: t("hero.b3") },
    { icon: <Lucide.CreditCard size={15} strokeWidth={2} className="text-emerald-700 shrink-0" />, t: t("hero.b4"), href: "/financiamento", tooltip: "Intermediação prestada por Dental Council, Lda — Reg. n.º 0004211 (BdP) — sujeito a aprovação" },
    { icon: <Lucide.AlertCircle size={15} strokeWidth={2} className="text-red-500 shrink-0" />, t: t("hero.b5") },
    { icon: <Lucide.Globe size={15} strokeWidth={2} className="text-brand-500 shrink-0" />, t: t("hero.b6") },
  ];

  const techItems = [
    { icon: "ScanLine", k: 1 }, { icon: "Smile", k: 2 }, { icon: "Brain", k: 3 },
    { icon: "Compass", k: 4 }, { icon: "Radiation", k: 5 }, { icon: "ScanFace", k: 6 },
  ];

  const paymentItems = [
    { icon: "CreditCard", k: 1 }, { icon: "CalendarClock", k: 2 }, { icon: "ShieldCheck", k: 3 },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section id="hero" className="relative pt-44 pb-24 lg:py-0 lg:min-h-[820px] overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-50 via-white to-white" />
        <div className="absolute top-20 -right-32 w-[28rem] h-[28rem] bg-gold-100/40 rounded-full blur-3xl -z-10" />
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] bg-brand-100/30 rounded-full blur-3xl -z-10" />

        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[48%] overflow-hidden">
          <img src="https://customer-assets.emergentagent.com/job_barlavento-dental/artifacts/ys02gdc3_download%20%281%29.png" alt="Fachada Instituto Clínico do Barlavento Portimão" className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-32 right-8 bg-brand-500 text-white rounded-xl shadow-xl p-4 flex items-center gap-3 z-10">
            <Lucide.Award size={22} className="text-gold-300" />
            <div className="text-xs leading-tight"><p className="font-semibold">{t("hero.years")}</p><p className="opacity-80">{t("hero.yearsLabel")}</p></div>
          </div>
        </div>

        <div className="container-icb relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center lg:pt-44 lg:pb-32">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-50 border border-gold-200 text-gold-700 text-xs font-medium tracking-wider uppercase mb-6">
              <Lucide.Sparkles size={14} /> {t("hero.topBadge")}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-brand-500 text-balance">
              {t("hero.title.pre")} <em className="text-gold-500 not-italic">{t("hero.title.highlight")}</em> {t("hero.title.post")}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{t("hero.subtitle")}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-cta-marcar" size="lg" onClick={() => scrollTo("marcacao")} className="bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold px-8 h-12 shadow-lg shadow-gold-200 hover:-translate-y-0.5 transition-all">
                {t("cta.marcar")} <ArrowRight size={18} className="ml-2" />
              </Button>
              <a href={`tel:+${CLINIC.whatsappRaw}`} onClick={() => trackEvent("click_phone", { source: "hero" })} data-testid="hero-cta-call">
                <Button size="lg" variant="outline" className="border-brand-200 text-brand-500 hover:bg-brand-50 h-12 px-8 w-full">
                  <Lucide.Smartphone size={16} className="mr-2" /> {CLINIC.whatsapp}
                </Button>
              </a>
            </div>
            <div className="mt-9 grid grid-cols-2 gap-2.5 max-w-xl" data-testid="hero-badges">
              {heroBadges.map((b, i) => {
                const baseCls = "inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-slate-50/80 border border-slate-200/70 text-slate-700 text-[12.5px] font-medium text-center";
                if (b.href) {
                  return (
                    <a
                      key={i}
                      href={b.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={b.tooltip}
                      data-testid="hero-badge-financing"
                      onClick={() => trackEvent("click_financing", { source: "hero_badge" })}
                      className={`${baseCls} hover:border-brand-300 hover:bg-white hover:text-brand-600 transition-colors cursor-pointer`}
                    >
                      {b.icon} <span>{b.t}</span>
                    </a>
                  );
                }
                return (
                  <div key={i} className={baseCls}>
                    {b.icon} <span>{b.t}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={150} className="lg:hidden">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-200/50 aspect-[4/5]">
              <img src="https://customer-assets.emergentagent.com/job_barlavento-dental/artifacts/ys02gdc3_download%20%281%29.png" alt="Fachada Instituto Clínico do Barlavento Portimão" className="w-full h-full object-cover object-center" loading="eager" />
              <div className="absolute top-4 right-4 bg-brand-500 text-white rounded-xl shadow-xl p-3 flex items-center gap-2">
                <Lucide.Award size={18} className="text-gold-300" />
                <div className="text-[11px] leading-tight"><p className="font-semibold">{t("hero.years")}</p><p className="opacity-80">{t("hero.yearsLabel")}</p></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section id="servicos" className="py-24 lg:py-32 bg-white">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">{t("services.eyebrow")}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-500">{t("services.title")}</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {SERVICE_KEYS.map((s, i) => {
              const Icon = Lucide[s.icon] || Lucide.Sparkles;
              const isLast = i === SERVICE_KEYS.length - 1;
              const orphan = isLast && SERVICE_KEYS.length % 3 === 1 ? "sm:col-span-2 lg:col-span-1 lg:col-start-2" : "";
              // Internal links: deep-link to dedicated SEO landing pages for key services
              const landingMap = {
                implantes: SERVICE_LANDING.implantes.slug,
                ortoEsp: SERVICE_LANDING.ortodontia.slug,
                alinhadores: SERVICE_LANDING.alinhadores.slug,
                est: SERVICE_LANDING.estetica.slug,
                bran: SERVICE_LANDING.estetica.slug,
              };
              const landingSlug = landingMap[s.key];
              // Featured services with a custom CTA at the bottom of the card
              const featuredCtaKey = ["implantes", "ortoEsp", "alinhadores", "est"].includes(s.key) ? s.key : null;
              const cardClass = "w-full h-full group flex flex-col items-center gap-4 p-6 rounded-2xl hover:bg-slate-50 transition-all duration-300 hover:-translate-y-1 text-center";
              const inner = (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-brand-50 text-brand-500 inline-flex items-center justify-center group-hover:bg-brand-500 group-hover:text-white transition-all duration-300">
                    <Icon size={28} strokeWidth={1.5} />
                  </div>
                  <p className="font-serif text-brand-500 text-lg leading-tight">{t(`svc.${s.key}.name`)}</p>
                  <p className="text-sm text-slate-500 leading-relaxed">{t(`svc.${s.key}.desc`)}</p>
                  {featuredCtaKey && (
                    <span
                      data-testid={`service-cta-${s.key}`}
                      className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-gold-600 group-hover:text-brand-500 transition-colors border-t border-slate-100 pt-4 w-full justify-center"
                    >
                      {t(`svc.${s.key}.cta`)}
                      <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  )}
                </>
              );
              return (
                <Reveal key={s.key} delay={i * 60} className={orphan}>
                  {landingSlug ? (
                    <Link to={`/${landingSlug}`} data-testid={`service-${s.key}`} className={cardClass}>{inner}</Link>
                  ) : (
                    <button onClick={() => scrollTo("marcacao")} data-testid={`service-${s.key}`} className={cardClass}>{inner}</button>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FACILIDADES DE PAGAMENTO */}
      <section id="pagamento" className="py-24 lg:py-32 bg-white">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">{t("pay.eyebrow")}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-500 mb-4">{t("pay.title")}</h2>
              <p className="text-slate-500 leading-relaxed">{t("pay.subtitle")}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {paymentItems.map((p, i) => {
              const Icon = Lucide[p.icon] || Lucide.CheckCircle2;
              return (
                <Reveal key={p.k} delay={i * 100}>
                  <div data-testid={`payment-${i}`} className="bg-white p-8 rounded-2xl border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 h-full">
                    <div className="w-14 h-14 rounded-xl bg-gold-50 text-gold-500 inline-flex items-center justify-center mb-6"><Icon size={26} strokeWidth={1.5} /></div>
                    <h3 className="font-serif text-2xl text-brand-500 mb-3">{t(`pay.${p.k}.title`)}</h3>
                    <p className="text-slate-500 leading-relaxed">{t(`pay.${p.k}.desc`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <p className="text-[11px] text-slate-400 text-center mt-10 max-w-3xl mx-auto leading-relaxed">{t("pay.legal")}</p>
            <div className="mt-6 max-w-3xl mx-auto bg-slate-50 border border-slate-200/70 rounded-xl p-5 text-center text-[12px] text-slate-600 leading-relaxed" data-testid="financing-banner">
              <p>
                Intermediação de crédito prestada por <strong className="text-brand-500">Dental Council, Lda</strong> — Reg. n.º <strong className="text-brand-500">0004211</strong> (Banco de Portugal). Consulte a documentação pré-contratual na nossa{" "}
                <Link to="/financiamento" data-testid="payment-section-financing-link" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_financing", { source: "pay_section_banner" })} className="text-brand-500 hover:text-gold-500 underline-offset-2 hover:underline font-medium">
                  página de Financiamento
                </Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TECNOLOGIA */}
      <section id="tecnologia" className="py-24 lg:py-32 bg-slate-50/60">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">{t("tech.eyebrow")}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-500 mb-4">{t("tech.title")}</h2>
              <p className="text-slate-500 leading-relaxed">{t("tech.subtitle")}</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {techItems.map((it, i) => {
              const Icon = Lucide[it.icon] || Lucide.Sparkles;
              return (
                <Reveal key={it.k} delay={i * 80}>
                  <div data-testid={`tech-${i}`} className="bg-white p-7 rounded-2xl border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 h-full hover:-translate-y-1 flex flex-col">
                    <div className="w-14 h-14 rounded-xl bg-brand-50 text-brand-500 inline-flex items-center justify-center mb-5"><Icon size={26} strokeWidth={1.5} /></div>
                    <h3 className="font-serif text-xl lg:text-2xl text-brand-500 mb-3 leading-tight">{t(`tech.${it.k}.title`)}</h3>
                    <p className="text-slate-500 leading-relaxed text-[15px]">{t(`tech.${it.k}.desc`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORQUÊ NÓS */}
      <section id="porque-nos" className="py-24 lg:py-32 bg-slate-50/60">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">{t("reasons.eyebrow")}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-500">{t("reasons.title")}</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {REASON_KEYS.map((r, i) => {
              const Icon = Lucide[r.icon] || Lucide.CheckCircle2;
              return (
                <Reveal key={r.idx} delay={i * 100}>
                  <div data-testid={`reason-${i}`} className="bg-white p-8 rounded-2xl border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 h-full">
                    <div className="w-14 h-14 rounded-xl bg-gold-50 text-gold-500 inline-flex items-center justify-center mb-6"><Icon size={26} strokeWidth={1.5} /></div>
                    <h3 className="font-serif text-2xl text-brand-500 mb-3">{t(`reasons.${r.idx}.title`)}</h3>
                    <p className="text-slate-500 leading-relaxed">{t(`reasons.${r.idx}.desc`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTEMUNHOS */}
      <section id="testemunhos" className="py-24 lg:py-32 bg-white">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">{t("tests.eyebrow")}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-500">{t("tests.title")}</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Reveal>
              <div data-testid="reviews-card-doctoralia" className="bg-white border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 rounded-2xl p-8 lg:p-10 text-center flex flex-col items-center h-full">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex text-gold-400">{Array.from({length:5}).map((_,i)=>(<Star key={i} size={22} fill="currentColor" />))}</div>
                  <span className="font-serif text-2xl text-brand-500 ml-2">5.0</span>
                </div>
                <p className="text-slate-600">{t("tests.docText")}</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mt-2 mb-7">{t("tests.docCount")}</p>
                <a href="https://www.doctoralia.com.pt/clinicas/instituto-clinico-do-barlavento-medico-e-dentario" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_doctoralia")} data-testid="doctoralia-reviews-link" className="mt-auto">
                  <Button className="bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold px-6 h-11 shadow-md shadow-gold-200 hover:-translate-y-0.5 transition-all">
                    {t("tests.docBtn")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </a>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div data-testid="reviews-card-google" className="bg-white border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 rounded-2xl p-8 lg:p-10 text-center flex flex-col items-center h-full">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex text-gold-400 items-center">
                    {Array.from({length:4}).map((_,i)=>(<Star key={i} size={22} fill="currentColor" />))}
                    <Lucide.StarHalf size={22} fill="currentColor" />
                  </div>
                  <span className="font-serif text-2xl text-brand-500 ml-2">4.4</span>
                </div>
                <p className="text-slate-600">{t("tests.gText")}</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mt-2 mb-7">{t("tests.gCount")}</p>
                <a href="https://www.google.com/search?q=instituto+clinico+do+barlavento" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_google_reviews")} data-testid="google-reviews-link" className="mt-auto">
                  <Button className="bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold px-6 h-11 shadow-md shadow-gold-200 hover:-translate-y-0.5 transition-all">
                    {t("tests.gBtn")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARCAÇÃO */}
      <section id="marcacao" className="py-24 lg:py-32 bg-brand-500 text-white relative overflow-hidden">
        <span id="agendamento" aria-hidden="true" className="block -mt-24 pt-24" />
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 hidden lg:block">
          <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=900&q=80" alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="container-icb relative">
          <div className="max-w-2xl mx-auto">
            <Reveal>
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-400/15 border border-gold-300/30 text-gold-300 text-xs font-medium tracking-wider uppercase mb-5" data-testid="urgency-badge">
                  <Lucide.AlertCircle size={14} /> {t("marc.urgency")}
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-gold-300 mb-3">{t("marc.eyebrow")}</p>
                <h2 className="font-serif text-4xl md:text-5xl mb-4">{t("marc.title")}</h2>
                <p className="text-slate-200/90 leading-relaxed">{t("marc.subtitle")}</p>
              </div>
            </Reveal>

            <Reveal delay={100}>
              {done ? (
                <div data-testid="marcacao-success" className="bg-white text-brand-500 p-10 rounded-2xl text-center shadow-2xl">
                  <div className="w-16 h-16 rounded-full bg-gold-50 text-gold-500 inline-flex items-center justify-center mb-5"><CheckCircle2 size={32} /></div>
                  <h3 className="font-serif text-3xl mb-3">{t("marc.success.title")}</h3>
                  <p className="text-slate-600 mb-6">{t("marc.success.desc")}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a href={`https://wa.me/${CLINIC.whatsappRaw}`} target="_blank" rel="noreferrer" onClick={() => trackEvent("click_whatsapp", { source: "marcacao_success" })}>
                      <Button className="bg-[#25D366] hover:bg-[#1ebf5a] text-white w-full"><MessageCircle size={16} className="mr-2" /> WhatsApp</Button>
                    </a>
                    <Button variant="outline" onClick={() => setDone(false)}>{t("marc.success.newBtn")}</Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="marcacao-form" className="bg-white text-slate-700 p-7 md:p-10 rounded-2xl shadow-2xl space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>{t("form.name")}</Label><Input data-testid="marc-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
                    <div><Label>{t("form.phone")}</Label><Input data-testid="marc-phone" required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
                  </div>
                  <div>
                    <Label>{t("form.service")}</Label>
                    <Select value={form.service} onValueChange={(v) => setForm({...form, service: v})} required>
                      <SelectTrigger data-testid="marc-service"><SelectValue placeholder={t("form.servicePh")} /></SelectTrigger>
                      <SelectContent>{appointmentOptions.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("form.email")}</Label><Input data-testid="marc-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
                  <div><Label>{t("form.message")}</Label><Textarea data-testid="marc-message" rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder={t("form.messagePh")} /></div>
                  <div className="flex items-start gap-2.5 pt-1">
                    <Checkbox id="marc-consent" data-testid="marc-consent" checked={consent} onCheckedChange={setConsent} className="mt-0.5 border-slate-300 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500" />
                    <label htmlFor="marc-consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer select-none">
                      {t("form.consent.pre")} <Link to="/politica-privacidade" className="text-brand-500 underline hover:text-gold-500" target="_blank" rel="noopener noreferrer">{t("form.consent.link")}</Link> {t("form.consent.post")}
                    </label>
                  </div>
                  <Button data-testid="marc-submit" disabled={loading} className="w-full bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold h-12 text-base disabled:opacity-60">
                    {loading ? t("form.submitting") : <>{t("form.submit")} <ArrowRight size={16} className="ml-2" /></>}
                  </Button>
                </form>
              )}
            </Reveal>

            <Reveal delay={200}>
              <div className="text-center mt-8">
                <p className="text-sm text-slate-300/80 mb-3">{t("marc.prefer")}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <a href={`https://wa.me/${CLINIC.whatsappRaw}?text=${encodeURIComponent("Olá, gostaria de marcar uma consulta.")}`} target="_blank" rel="noreferrer" onClick={() => trackEvent("click_whatsapp", { source: "marcacao_section" })} className="flex-1">
                    <Button data-testid="cta-whatsapp" className="w-full bg-[#25D366] hover:bg-[#1ebf5a] text-white"><MessageCircle size={16} className="mr-2" /> WhatsApp</Button>
                  </a>
                  <a href={`tel:+${CLINIC.whatsappRaw}`} onClick={() => trackEvent("click_phone", { source: "marcacao_section_movel" })} className="flex-1">
                    <Button data-testid="cta-movel" className="w-full bg-brand-500 hover:bg-brand-600 text-white border border-brand-400"><Lucide.Smartphone size={16} className="mr-2" /> {CLINIC.whatsapp}</Button>
                  </a>
                  <a href={`tel:${CLINIC.phoneRaw}`} onClick={() => trackEvent("click_phone", { source: "marcacao_section_fixo" })} className="flex-1">
                    <Button data-testid="cta-fixo" variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 bg-transparent"><Phone size={16} className="mr-2" /> {CLINIC.phone}</Button>
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
